import React from "react";
import Navbar from "../components/navbar";
import Hero from "../components/hero";
import Footer from "../components/footer";
import Accordion from "../components/accordion";
import "../styles/universal.css";
import "../styles/faqPage.css";

const FaqPage = () => {
  const faqSections = [
    {
      title: "How will I know if counselling will work for me?",
      content: (
        <>
          <p>
            It's completely normal to wonder if counselling will make a
            difference, especially if you've been struggling for a while, or
            have tried to figure things out on your own.
          </p>
          <p>
            Counselling isn't about someone "fixing" you. It's about having a
            safe, confidential space where you can talk things through,
            understand yourself better, and learn ways to cope or grow.
          </p>
          <p>
            Many people find that just having someone truly listen, without
            judgment, already helps lighten the load. Everyone's experience is
            unique, and it can sometimes get a bit messy and uncomfortable, but
            most people who've tried it believe that counselling really does
            help.
          </p>
        </>
      ),
    },
    {
      title:
        "How can I tell if a mental health professional is a good fit for me?",
      content: (
        <>
          <p>
            A good fit is about connection and comfort. You should feel heard,
            respected, and get the impression that it will be safe to open up,
            even if it takes a few sessions to get there. A good counsellor will
            work with you, not talk at you. You don't need to feel an instant
            bond, but you should sense genuine care, understanding, and
            professionalism. If you feel uneasy or judged, it's okay to try
            someone else.
          </p>
        </>
      ),
    },
    {
      title: "What can I expect from my first session?",
      content: (
        <>
          <p>
            Your first session is like having a chat and getting to know each
            other. There's no pressure to say everything at once, it's a space
            for you to talk about what's been going on, what's brought you to
            counselling, and what you'd like to get out of it.
          </p>
          <p>
            We'll talk a bit about your background, your goals, and what's been
            feeling difficult lately. I will guide the conversation with gentle
            questions to help you explore what's been on your mind. You can ask
            any questions you have about how counselling works, confidentiality,
            or anything else that's on your mind.
          </p>
          <p>
            By the end of the session, you'll have a clearer idea of how we can
            work together, and whether it feels like a good fit.
          </p>
        </>
      ),
    },
    {
      title: "Is my session confidential?",
      content: (
        <>
          <p>
            Yes. What you share in therapy is kept private and confidential. The
            only exceptions are if there's a concern about serious harm to
            yourself or someone else, or if required by law. These limits are
            explained clearly in your first session, so you always know where
            you stand.
          </p>
          <p>
            Confidentiality helps create a safe space for honesty, trust, and
            real growth.
          </p>
        </>
      ),
    },
    {
      title:
        "What is the difference between a registered counsellor, psychologist, psychiatrist, social workers and other mental health practitioners, and how do I know which to choose?",
      content: (
        <>
          <p>
            Different mental health needs call for different kinds of
            professionals, and the South African mental health landscape
            reflects this. Who you choose to walk your mental health journey
            with you depends on what you need.
          </p>
          <p>
            <strong>Registered Counsellors</strong> are often the first point of
            formal mental health care for individuals, couples, and families.
            They provide short‑term, evidence‑based interventions to support
            personal growth, resilience, and wellbeing.
          </p>
          <p>
            While Registered Counsellors do not diagnose mental health disorders
            or prescribe medication, they are trained to recognise when
            specialised care is needed and will refer clients to a Psychologist
            or Psychiatrist for ongoing or clinical treatment.
          </p>
          <p>
            <strong>Psychologists</strong> are licensed to diagnose and treat
            mental health disorders and give long-term therapy. Psychologists
            specialise in either Clinical, Counselling or Educational
            psychology. This determines their area of expertise, scope of work
            and limitations. Clinical Psychologists are trained to deal with
            serious mental health conditions. A Counselling Psychologist is
            trained at clinical level, even though their orientation is often
            more relational and developmental. Educational Psychologists usually
            focus on clients with learning challenges.
          </p>
          <p>
            A <strong>Psychiatrist</strong> is a medical doctor who can
            prescribe medication and manage mental health conditions that may
            need medical treatment.
          </p>
          <p>
            <strong>Social Workers</strong> work across a broad spectrum of
            services, such as mediation, adoption, court reports. They may also
            provide family, couples and individual counselling.
          </p>
          <p>
            The above professionals are able to register with the Board of
            Healthcare Funders (BHF), and if you are covered by a medical aid
            for mental health services, you may claim for their services.
          </p>
          <p>
            Your rights are protected by the fact that Psychiatrists,
            Psychologists and Registered Counsellors are registered with the
            Health Professions Council of South Africa (HPCSA), while social
            workers register with the South African Council for Social Service
            Professions (SACSSP), which are statutory bodies. Anybody who
            unethically frames themselves as a Registered Counsellor,
            Psychologist or Psychiatrist, should be reported.
          </p>
          <p>
            <strong>Wellness Counsellors</strong> offer nonclinical support and
            focus on overall mental well- being. They are registered with the
            ASCHP (Association for Supportive Counsellors and Holistic
            Practitioners). ASCHP functions as a voluntary professional body.
          </p>
          <p>
            <strong>Pastoral Counsellors</strong> offer faith-based
            psychological care.
          </p>
          <p>
            Before starting counselling, take time to research your
            practitioner. Ask about their credentials, registration with
            professional bodies, and the types of services legislation allows
            them to offer.
          </p>
        </>
      ),
    },
    {
      title:
        "What therapeutic approaches (psychological modalities) do you use?",
      content: (
        <>
          <p>
            I use an integrated approach, which means I draw from several
            evidence-based methods depending on your needs, goals, and
            personality. There's no "one-size-fits- all" in counselling. We'll
            find what works best for you.
          </p>
          <p>Here's a brief overview of the main approaches I use:</p>
          <p>
            <strong>Person-Centred Therapy:</strong> A supportive,
            non-judgmental approach that helps you explore your thoughts and
            feelings at your own pace. It's about creating a safe, trusting
            space where you can understand yourself better and build confidence.
          </p>
          <p>
            <strong>Solution-Focused Brief Therapy (SFBT):</strong> A
            short-term, goal-oriented approach that focuses on what's working,
            rather than what's wrong. It helps you identify strengths, set
            realistic goals, and make small, meaningful changes.
          </p>
          <p>
            <strong>BrainWorking Recursive Therapy (BWRT®):</strong> A fast,
            effective method that helps the brain respond differently to past
            experiences, fears, or triggers. It's often used for anxiety,
            trauma, phobias, and performance issues.
          </p>
          <p>
            <strong>Behavioural Therapy:</strong> Used when patterns of
            behaviour are contributing to emotional distress. This approach
            focuses on developing practical coping skills and healthy habits
            that support lasting change.
          </p>
          <p>
            Together, these approaches create a flexible and personalised
            framework that help you move toward understanding, stability, and
            growth.
          </p>
        </>
      ),
    },
    {
      title: "Do you do psychometric testing?",
      content: (
        <>
          <p>
            Yes. I provide psychometric testing to assist grade 9 learners with
            subject choice selection, grade 11 learners with career guidance and
            adults to assess their career path.
          </p>
          <p>
            We can also use psychometric testing to gain a deeper understanding
            of areas like emotional functioning, personality, learning, or
            attention. This helps in self- understanding and the path we need to
            follow.
          </p>
        </>
      ),
    },
    {
      title: "Do you see couples or families?",
      content: (
        <>
          <p>
            I generally work with individuals. My focus is on creating a
            one-on-one space where you can explore your own thoughts, feelings,
            and experiences in depth. If couples or family therapy would be more
            appropriate, I'm happy to recommend trusted professionals who
            specialise in that type of work.
          </p>
        </>
      ),
    },
    {
      title:
        "If my child comes for counselling, will I be involved? Will I know what's going on?",
      content: (
        <>
          <p>
            Yes, but in a balanced way. For children and teens, parental
            involvement is important, but counselling also needs to be a safe,
            private space. We'll keep you informed about general progress and
            themes, while respecting your child's confidentiality. The goal is
            to support your child. If there are concerns about safety or
            well-being, parents are always brought in immediately.
          </p>
        </>
      ),
    },
    {
      title: "I can't afford mental health care. Where can I go?",
      content: (
        <>
          <p>There are a few places that you can go to for help.</p>
          <p>
            <strong>Lifeline</strong> is a national counselling line that is
            available 24/7 and offers support from lay counsellors.
          </p>
          <p>
            <strong>
              The South African Depression and Anxiety Group (SADAG)
            </strong>{" "}
            is a non-profit that offers support groups, telephonic counselling
            and educational resources around mental illness.
          </p>
          <p>
            <strong>FAMSA</strong> is a non-profit organisation offering both
            individual, couples and family therapy. They have offices
            distributed all over the country.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="faq-page">
      <Navbar />
      <Hero
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about counselling, confidentiality, and getting started."
      />

      <div className="faq-content">
        <div className="faq-intro">
          <p>
            If you have a question that isn't answered here, please feel free to{" "}
            <a href="/contact">contact me</a> directly.
          </p>
        </div>
        <Accordion sections={faqSections} />
      </div>

      <Footer />
    </div>
  );
};

export default FaqPage;
